import { Component, HostBinding, ChangeDetectionStrategy, ViewEncapsulation, OnInit, Inject, ViewChild, ElementRef, OnDestroy,
    ComponentRef, ChangeDetectorRef } from '@angular/core';
import { DomPortalOutlet, ComponentPortal } from '@angular/cdk/portal';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { BaseDirective } from '../../base.directive';
import { EuiDialogConfig, EuiDialogInjectedComponentInstances } from './models/dialog.config.model';
import { EuiDynamicComponentService } from '../../services/eui-dynamic-component.service';

@Component({
    templateUrl: './eui-dialog.component.html',
    selector: 'eui-dialog',
    styleUrls: ['./styles/_index.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    encapsulation: ViewEncapsulation.None,
})
export class EuiDialogComponent extends BaseDirective implements OnInit, OnDestroy {

    @ViewChild('euiDialogHeader') euiDialogHeader: ElementRef<HTMLDivElement>;
    @ViewChild('euiDialogBody') euiDialogBody: ElementRef<HTMLDivElement>;
    @ViewChild('euiDialogFooter') euiDialogFooter: ElementRef<HTMLDivElement>;

    public headerComponent: {
        portalHost: DomPortalOutlet, portal: ComponentPortal<any>, portalComponentInstance: ComponentRef<any> } = null;
    public bodyComponent: { portalHost: DomPortalOutlet, portal: ComponentPortal<any>, portalComponentInstance: ComponentRef<any> } = null;
    public footerComponent: {
        portalHost: DomPortalOutlet, portal: ComponentPortal<any>, portalComponentInstance: ComponentRef<any> } = null;

    @HostBinding('class')
    get cssClasses() {
        return [
            super.getCssClasses('eui-dialog'),
        ].join(' ').trim();
    }

    private destroy$: Subject<boolean> = new Subject<boolean>();

    constructor(private dialogRef: MatDialogRef<any>,
                @Inject(MAT_DIALOG_DATA) public data: EuiDialogConfig,
                private euiDynamicComponentService: EuiDynamicComponentService, private cd: ChangeDetectorRef) {
        super();
    }

    ngOnInit() {
        this.dialogRef.disableClose = true;
        setTimeout(() => {
            if (this.data.headerComponent) {
                this.headerComponent = this.euiDynamicComponentService.attach(this.data.headerComponent.component,
                    this.euiDialogHeader,
                    this.data.headerComponent.config ? Object.assign(this.data.headerComponent.config, {
                        matDialogRef: this.dialogRef,
                    }) : Object.assign({}, {
                        matDialogRef: this.dialogRef,
                    }));
            }
            if (this.data.bodyComponent) {
                this.bodyComponent = this.euiDynamicComponentService.attach(this.data.bodyComponent.component,
                    this.euiDialogBody,
                    this.data.bodyComponent.config ? Object.assign(this.data.bodyComponent.config, {
                        matDialogRef: this.dialogRef,
                    }) : Object.assign({}, {
                        matDialogRef: this.dialogRef,
                    }));
            }
            if (this.data.footerComponent) {
                this.footerComponent = this.euiDynamicComponentService.attach(this.data.footerComponent.component,
                    this.euiDialogFooter,
                    this.data.footerComponent.config ? Object.assign(this.data.footerComponent.config, {
                        matDialogRef: this.dialogRef,
                    }) : Object.assign({}, {
                        matDialogRef: this.dialogRef,
                    }));
            }

            this.cd.detectChanges();
        });

        if (this.data.isHandleCloseOnClickOutside) {
            this.dialogRef.backdropClick().pipe(takeUntil(this.destroy$)).subscribe((value) => {
                this.data.onClickOutside(this.dialogRef, new EuiDialogInjectedComponentInstances({
                    headerComponent: this.headerComponent,
                    bodyComponent: this.bodyComponent,
                    footerComponent: this.footerComponent,
                }));
            });
        }
    }

    ngOnDestroy() {
        if (this.bodyComponent) {
            this.euiDynamicComponentService.detach(this.bodyComponent.portalHost);
            this.data.bodyComponent.component = null;
            this.data.bodyComponent.config = null;
        }
        if (this.footerComponent) {
            this.euiDynamicComponentService.detach(this.footerComponent.portalHost);
            this.data.footerComponent.component = null;
            this.data.footerComponent.config = null;
        }

        this.destroy$.next(true);
        this.destroy$.unsubscribe();
    }

    public closeDialog() {
        if (this.data.onClose) {
            this.data.onClose(this.dialogRef, new EuiDialogInjectedComponentInstances({
                headerComponent: this.headerComponent,
                bodyComponent: this.bodyComponent,
                footerComponent: this.footerComponent,
            }));
        }

        if (!this.data.isHandleCloseOnClose) {
            this.dialogRef.close();
        }
    }

    public onClick(value: boolean) {
        if (this.data.onClick) {
            this.data.onClick(value, new EuiDialogInjectedComponentInstances({
                headerComponent: this.headerComponent,
                bodyComponent: this.bodyComponent,
                footerComponent: this.footerComponent,
            }));
        }

        if (value) {
            if (this.data.onAccept) {
                this.data.onAccept(this.dialogRef, new EuiDialogInjectedComponentInstances({
                    headerComponent: this.headerComponent,
                    bodyComponent: this.bodyComponent,
                    footerComponent: this.footerComponent,
                }));
            }
        } else {
            if (this.data.onDismiss) {
                this.data.onDismiss(this.dialogRef, new EuiDialogInjectedComponentInstances({
                    headerComponent: this.headerComponent,
                    bodyComponent: this.bodyComponent,
                    footerComponent: this.footerComponent,
                }));
            }
        }

        if (value && !this.data.isHandleCloseOnAccept) {
            this.dialogRef.close();
        }

        if (!value && !this.data.isHandleCloseOnDismiss) {
            this.dialogRef.close();
        }
    }

}
